<template>
  <div class="card-modern">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Kertas Kerja Opname License (PC &amp; NB)</h3>
        <small class="text-muted">
          Opname lisensi software PC &amp; Notebook. Satu baris per asset (barcode digabung).
        </small>
      </div>
    </div>

    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button
        class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px"
        :disabled="exporting"
        @click="doExport"
      >
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else><i class="bi bi-file-earmark-excel me-1"></i>Export Excel</span>
      </button>

      <div style="width: 160px">
        <label class="form-label small text-muted mb-1">Tanggal Opname</label>
        <input
          v-model="filters.opname_date"
          type="date"
          class="form-control"
          style="border-radius: 8px"
        />
      </div>

      <div class="site-filter-ms" style="width: 220px; flex-shrink: 0">
        <label class="form-label small text-muted mb-1">Site / Cabang</label>
        <Multiselect
          v-model="filters.site_id"
          :options="sites"
          value-prop="mst_site_id"
          label="mst_site_name"
          :searchable="true"
          :can-clear="true"
          :close-on-select="true"
          no-options-text="Site tidak ditemukan"
          no-results-text="Site tidak ditemukan"
        />
      </div>

      <div style="width: 200px">
        <label class="form-label small text-muted mb-1">Cari</label>
        <input
          v-model="filters.search"
          class="form-control"
          style="border-radius: 8px"
          @keyup.enter="fetchData(1)"
        />
      </div>

      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button
        class="btn btn-sm bg-light border text-dark"
        style="border-radius: 8px; height: 38px"
        @click="resetFilter"
      >
        Reset
      </button>
    </div>

    <div v-if="!loading" class="mb-3">
      <span class="badge-modern badge-warning">
        {{ pagination.total }} asset aktif
      </span>
    </div>

    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th>No</th>
            <th>Jenis</th>
            <th>Barcode</th>
            <th>User</th>
            <th>Lokasi</th>
            <th>Divisi</th>
            <th>Merk / Type</th>
            <th>OS / Office</th>
            <th>Site</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>Memuat...
            </td>
          </tr>
          <tr v-else-if="!data.length">
            <td colspan="9" class="text-center text-muted py-4">Tidak ada data</td>
          </tr>
          <tr v-for="(item, i) in data" :key="item.asset_id">
            <td>{{ rowNum(i) }}</td>
            <td>{{ item.jenis_perangkat || "-" }}</td>
            <td style="white-space: pre-line; font-size: 0.8rem">{{ item.barcode_asset || "-" }}</td>
            <td>{{ item.user_name || "-" }}</td>
            <td>{{ item.lokasi_penempatan || "-" }}</td>
            <td>{{ item.divisi || "-" }}</td>
            <td>{{ [item.merk, item.tipe].filter(Boolean).join(" / ") || "-" }}</td>
            <td style="font-size: 0.8rem">
              {{ item.os_installer || "-" }} / {{ item.office_installer || "-" }}
            </td>
            <td>{{ item.site_name || "-" }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination.total > pagination.limit" class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">
        Halaman {{ pagination.page }} dari {{ totalPages }}
      </small>
      <nav>
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
            <a class="page-link" href="#" @click.prevent="fetchData(pagination.page - 1)">Prev</a>
          </li>
          <li class="page-item" :class="{ disabled: pagination.page >= totalPages }">
            <a class="page-link" href="#" @click.prevent="fetchData(pagination.page + 1)">Next</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import reportService from "@/services/reportOpnameLicenseService";
import siteService from "@/services/siteService";

const data = ref([]);
const sites = ref([]);
const loading = ref(false);
const exporting = ref(false);
const filters = reactive({
  search: "",
  site_id: null,
  opname_date: new Date().toISOString().slice(0, 10),
});
const pagination = reactive({ page: 1, limit: 10, total: 0 });
const totalPages = computed(() =>
  Math.max(1, Math.ceil(pagination.total / pagination.limit))
);

const buildParams = (extra = {}) => {
  const p = { page: pagination.page, limit: pagination.limit, ...extra };
  if (filters.search) p.search = filters.search;
  if (filters.site_id !== null && filters.site_id !== undefined && filters.site_id !== '') {
    p.site_id = filters.site_id;
    const s = sites.value.find((x) => String(x.mst_site_id) === String(filters.site_id));
    if (s) p.site_name = s.mst_site_name;
  }
  if (filters.opname_date) p.opname_date = filters.opname_date;
  return p;
};

const fetchData = async (page = 1) => {
  if (page < 1) return;
  loading.value = true;
  pagination.page = page;
  try {
    const res = await reportService.getList(buildParams({ page }));
    data.value = res.data.data || [];
    pagination.total = res.data.pagination?.total || 0;
  } catch (e) {
    console.error(e);
    alert("Gagal memuat data opname license");
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const resetFilter = () => {
  filters.search = "";
  filters.site_id = null;
  filters.opname_date = new Date().toISOString().slice(0, 10);
  fetchData(1);
};

const doExport = async () => {
  exporting.value = true;
  try {
    const res = await reportService.exportExcel(buildParams({ page: 1, limit: 0 }));
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "Opname_License.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert("Gagal export");
  } finally {
    exporting.value = false;
  }
};

const rowNum = (i) => (pagination.page - 1) * pagination.limit + i + 1;
const isActiveSite = (x) => {
  const val = x?.status ?? x?.is_active;
  return val === undefined || val === null ? true : Number(val) === 1;
};

const loadSites = async () => {
  try {
    const res = await siteService.getAll();
    const list = (res.data || [])
      .filter(isActiveSite)
      .sort((a, b) =>
        String(a.mst_site_name || "").localeCompare(
          String(b.mst_site_name || ""),
          "id",
          { sensitivity: "base" },
        ),
      );
    sites.value = list;
  } catch {
    sites.value = [];
  }
};

onMounted(() => {
  loadSites();
  fetchData(1);
});
</script>

<style scoped>
.site-filter-ms :deep(.multiselect) {
  min-height: 38px;
  font-size: 0.875rem;
}
.site-filter-ms :deep(.multiselect-input) {
  font-size: 0.875rem;
}
.site-filter-ms :deep(.multiselect-search) {
  font-size: 0.875rem;
}
</style>